import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleReviews } from '../../components/feature/GoogleReviews';

export default function SolarPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    tipoInmueble: '',
    consumoMensual: '',
    provincia: 'Granada',
    observaciones: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      // Guardar solicitud en localStorage
      const solicitudes = JSON.parse(localStorage.getItem('solicitudes') || '[]');
      const nuevaSolicitud = {
        id: Date.now().toString(),
        tipo: 'Estudio solar',
        fecha: new Date().toISOString(),
        estado: 'Recibida',
        nombre: formData.nombre,
        telefono: formData.telefono,
        email: formData.email,
        observaciones: `Tipo: ${formData.tipoInmueble} | Consumo: ${formData.consumoMensual} kWh | ${formData.provincia}`
      };
      
      solicitudes.push(nuevaSolicitud);
      localStorage.setItem('solicitudes', JSON.stringify(solicitudes));

      setIsSubmitting(false);
      alert('✅ ¡Solicitud recibida!\n\nTe contactaremos pronto para realizar tu estudio solar personalizado y gratuito.');
      navigate('/solicitudes');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-[#FF8F00] rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-sun-line text-white text-4xl"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-4">
            Convierte tu energía solar en ahorro real todos los meses
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Instalaciones solares, batería virtual y gestión de excedentes para hogares y empresas.
          </p>
          
          {/* Bullets de autoridad */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center text-left sm:text-center mb-8">
            <div className="flex items-center gap-2 justify-center">
              <i className="ri-check-line text-2xl text-[#00C853]"></i>
              <span className="text-gray-700 font-medium">Instalaciones solares a medida</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <i className="ri-check-line text-2xl text-[#00C853]"></i>
              <span className="text-gray-700 font-medium">Compensación de excedentes</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <i className="ri-check-line text-2xl text-[#00C853]"></i>
              <span className="text-gray-700 font-medium">Batería virtual para maximizar el ahorro</span>
            </div>
          </div>

          <button
            onClick={() => document.getElementById('formulario-solar')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center justify-center gap-2 bg-[#FF8F00] hover:bg-[#E68000] text-white font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
          >
            ☀️ Solicitar estudio solar gratuito
          </button>
        </div>

        {/* El Problema Real */}
        <div className="bg-white rounded-3xl p-8 md:p-12 mb-12 shadow-sm">
          <h2 className="text-3xl font-bold text-[#1F2937] mb-4 text-center">
            Tener placas no siempre significa ahorrar lo máximo
          </h2>
          <p className="text-lg text-gray-600 mb-6 text-center max-w-3xl mx-auto">
            Muchos hogares y empresas producen más energía de la que consumen en ciertos momentos. Si esos excedentes no se gestionan bien, el ahorro se pierde.
          </p>
          <p className="text-xl font-semibold text-[#FF8F00] text-center">
            Producir energía y no aprovecharla es como tirar dinero.
          </p>
        </div>

        {/* Placas Solares */}
        <div className="bg-gradient-to-br from-[#FF8F00] to-[#FF6F00] rounded-3xl p-8 md:p-12 mb-12 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Instalaciones de placas solares a medida
            </h2>
            <p className="text-lg text-white/90">
              Diseñamos instalaciones fotovoltaicas adaptadas a tu consumo real, tanto para viviendas como para empresas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-euro-circle-line text-4xl"></i>
              </div>
              <h3 className="font-bold text-xl mb-2">Reducción inmediata en la factura</h3>
              <p className="text-white/90">
                Empieza a ahorrar desde el primer mes de instalación
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-4xl"></i>
              </div>
              <h3 className="font-bold text-xl mb-2">Menor dependencia de la red</h3>
              <p className="text-white/90">
                Produce tu propia energía y reduce tu dependencia eléctrica
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-leaf-line text-4xl"></i>
              </div>
              <h3 className="font-bold text-xl mb-2">Energía limpia y sostenible</h3>
              <p className="text-white/90">
                Contribuye al medio ambiente con energía renovable
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-funds-line text-4xl"></i>
              </div>
              <h3 className="font-bold text-xl mb-2">Rentabilidad a medio y largo plazo</h3>
              <p className="text-white/90">
                Inversión que se amortiza con el ahorro generado
              </p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => document.getElementById('formulario-solar')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#FF8F00] font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              🏠🏢 Quiero placas solares
            </button>
          </div>
        </div>

        {/* Excedentes Solares */}
        <div className="bg-white rounded-3xl p-8 md:p-12 mb-12 shadow-sm">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#0A5FFF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-arrow-left-right-line text-[#0A5FFF] text-4xl"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-4">
              ¿Qué son los excedentes solares?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Los excedentes son la energía que produces y no consumes en ese momento. Esa energía se puede compensar en tu factura si el contrato está bien configurado.
            </p>
          </div>

          <div className="bg-[#F7F9FC] rounded-2xl p-8 mb-6">
            <h3 className="text-2xl font-bold text-[#1F2937] mb-6 text-center">
              Qué hacemos con tus excedentes
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0A5FFF] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-[#1F2937] mb-1">Analizamos tu producción y consumo</h4>
                  <p className="text-gray-600">Estudiamos cuánta energía produces y cuánta consumes en cada momento</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00C853] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-settings-3-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-[#1F2937] mb-1">Ajustamos la compensación de excedentes</h4>
                  <p className="text-gray-600">Configuramos tu contrato para que aproveches al máximo cada kWh</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF8F00] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-[#1F2937] mb-1">Optimizamos tu contrato eléctrico</h4>
                  <p className="text-gray-600">Buscamos la tarifa que mejor se adapte a tu producción solar</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xl font-semibold text-[#0A5FFF] text-center mb-6">
            Cada kWh excedente bien gestionado es ahorro directo.
          </p>

          <div className="text-center">
            <a
              href="https://wa.me/34696237625?text=Hola,%20quiero%20revisar%20mis%20excedentes%20solares"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#0A5FFF] hover:bg-[#0850D6] text-white font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              🔄 Revisar mis excedentes
            </a>
          </div>
        </div>

        {/* Batería Virtual */}
        <div className="bg-gradient-to-br from-[#0A5FFF] to-[#0850D6] rounded-3xl p-8 md:p-12 mb-12 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                ¿Qué es la batería virtual y por qué te interesa?
              </h2>
              <p className="text-lg mb-6 text-white/90">
                La batería virtual te permite acumular el valor económico de tus excedentes para descontarlo en futuras facturas.
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold mb-1">Aprovechas el 100% de tu energía</h4>
                    <p className="text-white/80 text-sm">Nada de lo que produces se desperdicia</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold mb-1">Reduces aún más tu factura</h4>
                    <p className="text-white/80 text-sm">El ahorro se multiplica mes a mes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold mb-1">Compensas meses con menos sol</h4>
                    <p className="text-white/80 text-sm">Tu saldo acumulado te protege en invierno</p>
                  </div>
                </div>
              </div>
              <p className="text-xl font-semibold mb-6">
                Tu energía sobrante se convierte en saldo a tu favor.
              </p>
              <a
                href="https://wa.me/34696237625?text=Hola,%20quiero%20información%20sobre%20batería%20virtual"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#0A5FFF] font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                🔋 Quiero batería virtual
              </a>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="text-center">
                <i className="ri-battery-charge-line text-6xl mb-4"></i>
                <p className="text-lg mb-4">
                  Almacena virtualmente hasta 12 meses de excedentes para usar cuando más lo necesites
                </p>
                <div className="bg-white/20 rounded-xl p-4">
                  <p className="text-sm font-medium">
                    Sin necesidad de baterías físicas. Todo el ahorro, sin la inversión en hardware.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Para Quién es Ideal */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-8 text-center">
            Este servicio es ideal si…
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-[#FF8F00]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-[#FF8F00] text-3xl"></i>
              </div>
              <h3 className="font-bold text-xl text-[#1F2937] mb-2">Tienes vivienda con consumo eléctrico</h3>
              <p className="text-gray-600">
                Ideal para hogares que quieren reducir su factura de luz
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-[#0A5FFF]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-building-line text-[#0A5FFF] text-3xl"></i>
              </div>
              <h3 className="font-bold text-xl text-[#1F2937] mb-2">Tienes una empresa o negocio</h3>
              <p className="text-gray-600">
                Reduce costes energéticos y mejora la rentabilidad
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-[#00C853]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-sun-line text-[#00C853] text-3xl"></i>
              </div>
              <h3 className="font-bold text-xl text-[#1F2937] mb-2">Ya tienes placas y no sabes si aprovechas los excedentes</h3>
              <p className="text-gray-600">
                Optimizamos tu instalación existente para maximizar el ahorro
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-[#FF8F00]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-[#FF8F00] text-3xl"></i>
              </div>
              <h3 className="font-bold text-xl text-[#1F2937] mb-2">Quieres reducir tu factura al mínimo</h3>
              <p className="text-gray-600">
                Combinamos todas las estrategias para el máximo ahorro
              </p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => document.getElementById('formulario-solar')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 bg-[#00C853] hover:bg-[#00B048] text-white font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              📊 Calcular ahorro solar
            </button>
          </div>
        </div>

        {/* Cómo Trabajamos */}
        <div className="bg-white rounded-3xl p-8 md:p-12 mb-12 shadow-sm">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-8 text-center">
            Así te ayudamos a ahorrar con energía solar
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#0A5FFF] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-3xl">1</span>
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-3">Estudio personalizado</h3>
              <p className="text-gray-600">
                Analizamos consumo, espacio disponible y hábitos energéticos
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#00C853] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-3xl">2</span>
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-3">Propuesta optimizada</h3>
              <p className="text-gray-600">
                Instalación, gestión de excedentes y batería virtual adaptados a ti
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#FF8F00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-3xl">3</span>
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-3">Gestión integral</h3>
              <p className="text-gray-600">
                Nos encargamos de todo el proceso de principio a fin
              </p>
            </div>
          </div>

          <div className="bg-[#00C853]/10 rounded-2xl p-6 text-center">
            <p className="text-lg font-semibold text-[#1F2937]">
              Si no es rentable para ti, te lo diremos claramente.
            </p>
          </div>
        </div>

        {/* Prueba Social */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-4 text-center">
            Clientes que ya aprovechan su energía solar
          </h2>
          <p className="text-lg text-gray-600 mb-8 text-center">
            Cada vez más hogares y empresas confían en soluciones solares bien gestionadas.
          </p>
          <GoogleReviews maxReviews={6} />
        </div>

        {/* Formulario */}
        <div id="formulario-solar" className="bg-white rounded-3xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold text-[#1F2937] mb-2 text-center">
            Solicita tu estudio solar gratuito
          </h2>
          <p className="text-gray-600 mb-8 text-center">
            Analizamos tu caso y te enviamos un presupuesto personalizado sin compromiso
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-[#1F2937] mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1F2937] mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors"
                  placeholder="600 000 000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1F2937] mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors"
                placeholder="tu@email.com"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-[#1F2937] mb-2">
                  Tipo de inmueble *
                </label>
                <select
                  required
                  value={formData.tipoInmueble}
                  onChange={(e) => setFormData({ ...formData, tipoInmueble: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors cursor-pointer"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Vivienda unifamiliar">Vivienda unifamiliar</option>
                  <option value="Piso/Apartamento">Piso/Apartamento</option>
                  <option value="Nave industrial">Nave industrial</option>
                  <option value="Local comercial">Local comercial</option>
                  <option value="Edificio completo">Edificio completo</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1F2937] mb-2">
                  Consumo mensual (kWh) *
                </label>
                <input
                  type="number"
                  required
                  value={formData.consumoMensual}
                  onChange={(e) => setFormData({ ...formData, consumoMensual: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors"
                  placeholder="Ej: 300"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1F2937] mb-2">
                Provincia *
              </label>
              <select
                required
                value={formData.provincia}
                onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors cursor-pointer"
              >
                <option value="Granada">Granada</option>
                <option value="Madrid">Madrid</option>
                <option value="Barcelona">Barcelona</option>
                <option value="Valencia">Valencia</option>
                <option value="Sevilla">Sevilla</option>
                <option value="Málaga">Málaga</option>
                <option value="Almería">Almería</option>
                <option value="Otra">Otra</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1F2937] mb-2">
                Observaciones (opcional)
              </label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0A5FFF] focus:outline-none transition-colors resize-none"
                placeholder="Cuéntanos más sobre tu proyecto..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.observaciones.length}/500 caracteres
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#FF8F00] hover:bg-[#E68000] disabled:bg-gray-300 text-white font-bold py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  Enviando...
                </>
              ) : (
                <>
                  🚀 Solicitar estudio solar gratuito
                </>
              )}
            </button>
          </form>
        </div>

        {/* FAQ */}
        <div className="mt-12 bg-white rounded-2xl p-8 shadow-sm">
          <h3 className="text-2xl font-bold text-[#1F2937] mb-6 text-center">
            Preguntas frecuentes
          </h3>
          <div className="space-y-4">
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-[#1F2937]">¿Cuánto puedo ahorrar con placas solares?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="p-4 text-gray-600">
                Dependiendo de tu consumo y ubicación, puedes ahorrar entre un 50% y un 70% en tu factura eléctrica. Con batería virtual, aprovechas el 100% de la energía generada.
              </p>
            </details>

            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-[#1F2937]">¿Qué pasa con la energía que no consumo?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="p-4 text-gray-600">
                Los excedentes se compensan en tu factura o se acumulan en tu batería virtual para usarlos cuando los necesites, incluso meses después.
              </p>
            </details>

            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-[#1F2937]">¿Cuánto tiempo tarda la instalación?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="p-4 text-gray-600">
                Una vez aprobado el proyecto, la instalación suele completarse en 1-2 días. El proceso completo desde el estudio hasta la puesta en marcha puede llevar 2-3 meses.
              </p>
            </details>

            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-[#1F2937]">¿Necesito baterías físicas?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="p-4 text-gray-600">
                No necesariamente. La batería virtual te permite acumular el valor de tus excedentes sin necesidad de invertir en baterías físicas, reduciendo costes iniciales.
              </p>
            </details>

            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-[#1F2937]">¿Hay subvenciones disponibles?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="p-4 text-gray-600">
                Sí, existen ayudas y subvenciones tanto a nivel estatal como autonómico. Te ayudamos con todos los trámites para que puedas beneficiarte de ellas.
              </p>
            </details>

            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-[#1F2937]">¿Funciona para empresas?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="p-4 text-gray-600">
                Sí, diseñamos instalaciones solares específicas para empresas, optimizando el ahorro según los horarios de consumo y la actividad del negocio.
              </p>
            </details>
          </div>
        </div>

        {/* CTA Final */}
        <div className="mt-12 bg-gradient-to-br from-[#FF8F00] to-[#FF6F00] rounded-3xl p-8 md:p-12 text-white text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Empieza a aprovechar tu energía solar al máximo
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Solicita un estudio solar gratuito y descubre cuánto puedes ahorrar.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => document.getElementById('formulario-solar')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#FF8F00] font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              🚀 Solicitar estudio solar gratuito
            </button>
            <a
              href="https://wa.me/34696237625?text=Hola,%20quiero%20información%20sobre%20energía%20solar"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Prefiero hablar por WhatsApp
            </a>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm text-center">
          <p className="text-gray-600 mb-4">
            ¿Tienes más preguntas? Contacta con nosotros
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <a
              href="https://wa.me/34696237625?text=Hola,%20quiero%20información%20sobre%20energía%20solar"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#00C853] hover:bg-[#00B048] text-white font-semibold px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              WhatsApp
            </a>
            <a
              href="tel:696237625"
              className="flex items-center gap-2 bg-[#0A5FFF] hover:bg-[#0850D6] text-white font-semibold px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl"></i>
              Llamar ahora
            </a>
          </div>
        </div>
      </div>

      {/* WhatsApp Sticky Button */}
      <a
        href="https://wa.me/34696237625?text=Hola,%20quiero%20información%20sobre%20energía%20solar"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full flex items-center justify-center shadow-2xl transition-all hover:scale-110 cursor-pointer"
        title="WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl"></i>
      </a>
    </div>
  );
}
